import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, User as UserIcon, Euro, CalendarDays } from "lucide-react";

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="mv-card p-5">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: color || "var(--mv-primary)" }} />
        <p className="mv-label">{label}</p>
      </div>
      <p className="font-display text-3xl">{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>{sub}</p>}
    </div>
  );
}

function Cell({ count, revenue }) {
  return (
    <td className="px-3 py-3 text-right whitespace-nowrap">
      <p className="font-medium">{count}</p>
      <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
    </td>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/cms/analytics/staff").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="p-12">Lade ...</div>;

  const fmtEuro = (n) => n.toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €";
  const monthLabel = new Date(data.month + "-01").toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <div className="p-8 lg:p-12 mv-fade-in" data-testid="analytics-page">
      <div className="mb-8">
        <p className="mv-label mb-2">Salon-Auswertung</p>
        <h1 className="font-display text-4xl flex items-center gap-3">
          <BarChart3 className="w-8 h-8" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
          Auswertung
        </h1>
        <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
          Termine & Umsatz pro Mitarbeiter — heute, im Monat ({monthLabel}) und insgesamt
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="analytics-totals">
        <Kpi icon={CalendarDays} label="Termine heute" value={data.totals.bookings_today}
          sub={`${fmtEuro(data.totals.revenue_today)} realisiert`} />
        <Kpi icon={CalendarDays} label={`Termine ${monthLabel}`} value={data.totals.bookings_month}
          sub={`${fmtEuro(data.totals.revenue_month)} realisiert`} color="var(--mv-secondary)" />
        <Kpi icon={Euro} label="Gesamtumsatz" value={fmtEuro(data.totals.revenue_total)}
          sub={`${data.totals.bookings_total} Termine insgesamt`} color="var(--mv-accent)" />
      </div>

      <div className="mv-card overflow-hidden" data-testid="analytics-table-wrapper">
        <table className="w-full">
          <thead>
            <tr className="text-xs uppercase tracking-wider" style={{ background: "var(--mv-surface)", color: "var(--mv-text-muted)" }}>
              <th className="px-4 py-3 text-left">Mitarbeiter</th>
              <th className="px-3 py-3 text-right">Heute<br /><span className="font-normal opacity-70">Termine / Umsatz</span></th>
              <th className="px-3 py-3 text-right">{monthLabel.split(" ")[0]}<br /><span className="font-normal opacity-70">Termine / Umsatz</span></th>
              <th className="px-3 py-3 text-right">Gesamt<br /><span className="font-normal opacity-70">Termine / Umsatz</span></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan="4" className="p-8 text-center text-sm" style={{ color: "var(--mv-text-muted)" }}>Noch keine Daten.</td></tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.staff_id || "unassigned"} className="border-t" style={{ borderColor: "var(--mv-border)" }} data-testid={`analytics-row-${r.staff_id || "unassigned"}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{ background: "var(--mv-surface)" }}>
                      {r.photo_url ? <img src={r.photo_url} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{r.name}</p>
                      {r.role && <p className="text-xs truncate" style={{ color: "var(--mv-text-muted)" }}>{r.role}</p>}
                    </div>
                  </div>
                </td>
                <Cell count={r.bookings_today} revenue={r.revenue_today} />
                <Cell count={r.bookings_month} revenue={r.revenue_month} />
                <Cell count={r.bookings_total} revenue={r.revenue_total} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 font-medium text-sm" style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}>
              <td className="px-4 py-3">Salon gesamt</td>
              <Cell count={data.totals.bookings_today} revenue={data.totals.revenue_today} />
              <Cell count={data.totals.bookings_month} revenue={data.totals.revenue_month} />
              <Cell count={data.totals.bookings_total} revenue={data.totals.revenue_total} />
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-xs mt-4" style={{ color: "var(--mv-text-muted)" }}>
        Hinweis: Umsatz zählt nur abgeschlossene Termine (Service + Zusatzleistungen abzüglich Treuepunkte-Rabatte).
      </p>
    </div>
  );
}
